import { storeToRefs } from 'pinia';
import { computed, ref, useTemplateRef } from 'vue';

import AddPartnerContactForm, {
  Contact,
} from '@/components/partners/AddPartner/AddPartnerContactForm.vue';
import { Company } from '@/components/partners/AddPartner/InvitePartnerSearch.vue';

import useActivationTeamCcGate from '@/composables/useActivationTeamCcGate';
import usePartnerManagement from '@/composables/usePartnerManagement';
import { usePartnersStore } from '@/stores';
import { findPartnerByDomains } from '@/utils/findPartnerByDomains';

export default function useInvitePartnerForm() {
  const { getDiscoverableTeam } = usePartnerManagement();
  const { partnerOrgs } = storeToRefs(usePartnersStore());

  const contactFormRef =
    useTemplateRef<InstanceType<typeof AddPartnerContactForm>>(
      'contactFormRef',
    );
  const selectedCompany = ref<Company | null>(null);
  const chosenContactId = ref<number | null>(null);
  const nonDiscoveredContactEmail = ref('');
  const inviteMessage = ref('');
  const activationTeamCc = ref(false);
  const potentialContacts = ref<Contact[]>([]);

  const isPartnerDiscoverable = computed(
    () => !!selectedCompany.value?.public_invite_code,
  );

  const { canShowActivationTeamCc } = useActivationTeamCcGate(selectedCompany);

  const existingPartner = computed(() =>
    findPartnerByDomains(partnerOrgs.value, [
      selectedCompany.value?.domain,
      selectedCompany.value?.clearbit_domain,
    ]),
  );

  const selectedContact = computed<Contact | string | undefined>(() =>
    chosenContactId.value
      ? potentialContacts.value.find((c) => c.id === chosenContactId.value)
      : nonDiscoveredContactEmail.value,
  );

  function resetFields() {
    selectedCompany.value = null;
    nonDiscoveredContactEmail.value = '';
    potentialContacts.value = [];
    chosenContactId.value = null;
    inviteMessage.value = '';
    activationTeamCc.value = false;
  }

  async function handleCompanyChanged({ company }: { company: Company }) {
    resetFields();
    if (!company?.name && !company?.domain) return;
    selectedCompany.value = company;
    if (company.public_invite_code) {
      potentialContacts.value = await getDiscoverableTeam({
        publicInviteCode: company.public_invite_code,
      });
    }
  }

  function buildContactPayload() {
    return typeof selectedContact.value === 'object' && selectedContact.value
      ? {
          email: selectedContact.value.email ?? null,
          first_name: selectedContact.value.first_name,
          last_name: selectedContact.value.last_name,
        }
      : { email: nonDiscoveredContactEmail.value || null };
  }

  return {
    contactFormRef,
    selectedCompany,
    chosenContactId,
    nonDiscoveredContactEmail,
    inviteMessage,
    activationTeamCc,
    canShowActivationTeamCc,
    potentialContacts,
    isPartnerDiscoverable,
    existingPartner,
    selectedContact,
    resetFields,
    handleCompanyChanged,
    buildContactPayload,
  };
}
